/**
 * Client-side IPFS gateway for public NFT cover URLs (same default as backend IPFS_GATEWAY_URL).
 */
import { getNftComicV2ChainId, getNftComicV2ImageBaseUriRaw, isSameNftComicV2Contract } from "../services/libraryNftComicV2Env";

const GATEWAY = String(
  process.env.REACT_APP_IPFS_GATEWAY_URL || "https://gateway.pinata.cloud/ipfs"
).replace(/\/+$/, "");

const BAD_SUBSTRINGS = [
  "dummy",
  "replacewithreal",
  "bafybeigdummy",
  "dummymerch",
  "dummyarmory",
  "dummyreader",
];

/**
 * Reject seed/placeholder NFT cover URLs (mirrors TreydoraEdBackend libraryNftDisplayImageUrl).
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isUsableDisplayImageUrl(url) {
  const s = String(url ?? "").trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  if (lower.endsWith(".json") || lower.includes(".json?")) return false;
  for (const b of BAD_SUBSTRINGS) {
    if (lower.includes(b)) return false;
  }
  if (/gateway\.pinata\.cloud\/ipfs\/bafybeigdummy/i.test(s)) return false;

  if (/^https?:\/\//i.test(s)) {
    try {
      const p = new URL(s).pathname.toLowerCase();
      if (p.endsWith(".json")) return false;
      return /\.(png|jpe?g|webp|gif)(\?|$)/i.test(p);
    } catch {
      return false;
    }
  }

  if (s.startsWith("ipfs://")) return true;
  if (s.startsWith("/") || s.startsWith("data:image/")) return true;

  if (!/[\\/]/.test(s) && s.length < 220) {
    for (const b of BAD_SUBSTRINGS) {
      if (lower.includes(b)) return false;
    }
    if (lower.endsWith(".json")) return false;
    return true;
  }

  return true;
}

/**
 * @param {string|null|undefined} cidOrUri raw CID, ipfs://..., or https URL
 * @returns {string|null}
 */
export function treydoraIpfsCoverToUrl(cidOrUri) {
  if (cidOrUri == null || typeof cidOrUri !== "string") return null;
  const t = cidOrUri.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.startsWith("ipfs://") ? t.slice(7).replace(/^\/+/, "") : t.replace(/^\/+/, "");
  if (!path) return null;
  return `${GATEWAY}/${path}`;
}

/** ipfs:// or https / relative / data / legacy pack keys (opaque strings pass through). */
function resolveMetadataImageField(raw) {
  const t = String(raw || "").trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("ipfs://")) return treydoraIpfsCoverToUrl(t);
  if (t.startsWith("/") || t.startsWith("data:")) return t;
  return t;
}

/** ERC-721 metadata `image` / `imageUrl` — bare CID or ipfs path → gateway when possible. */
function resolveNftMetadataImageField(raw) {
  const t = String(raw || "").trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("ipfs://")) return treydoraIpfsCoverToUrl(t);
  if (t.startsWith("/") || t.startsWith("data:")) return t;
  return treydoraIpfsCoverToUrl(t);
}

/**
 * `${NFT_COMIC_IMAGE_BASE_URI_V2}${tokenId}.png` when CRA env + metadata match configured V2 comic.
 * @param {object} meta entitlement.metadata-like
 * @param {string|number|null|undefined} rowTokenId LibraryEntitlement.tokenId
 * @returns {string|null}
 */
export function resolveClientV2GenesisTokenCoverPng(meta, rowTokenId) {
  const base = getNftComicV2ImageBaseUriRaw();
  if (!base) return null;
  const m = meta && typeof meta === "object" ? meta : {};
  if (String(m.productType || "").trim() !== "library_nft_mint") return null;
  if (!isSameNftComicV2Contract(m.contractAddress)) return null;
  const chain = Number(m.chainId);
  if (Number.isFinite(chain) && chain !== getNftComicV2ChainId()) return null;
  const tid = Number(
    m.assignedTokenId != null && m.assignedTokenId !== "" ? m.assignedTokenId : rowTokenId
  );
  if (!Number.isFinite(tid)) return null;
  const root = base.endsWith("/") ? base : `${base}/`;
  const url = `${root}${tid}.png`;
  return isUsableDisplayImageUrl(url) ? url : null;
}

/**
 * Preferred NFT cover from entitlement.metadata only (edition/asset fallbacks live in getLibraryNftCardCoverUrl).
 * Priority matches My Library / Collector Vault contract: token HTTPS URLs → nftMetadata image fields →
 * tokenImageUri → legacy imageUrl → coverImageCid.
 */
export function treydoraNftEntitlementCoverUrl(metadata) {
  const meta = metadata && typeof metadata === "object" ? metadata : {};
  const nftMeta = meta.nftMetadata && typeof meta.nftMetadata === "object" ? meta.nftMetadata : {};

  const candidates = [
    resolveMetadataImageField(meta.tokenImageUrl),
    resolveMetadataImageField(meta.tokenImageDisplayUrl),
    resolveNftMetadataImageField(nftMeta.imageUrl),
    resolveNftMetadataImageField(nftMeta.normalizedImageUrl),
    resolveNftMetadataImageField(nftMeta.image),
    resolveMetadataImageField(meta.tokenImageUri),
    resolveMetadataImageField(meta.imageUrl),
    treydoraIpfsCoverToUrl(String(meta.coverImageCid || "").trim()),
  ];

  for (const u of candidates) {
    if (isUsableDisplayImageUrl(u)) return u;
  }
  return null;
}
